import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo, WhatsAppIcon } from "./Logo";
import { CONTACT, waLink, WHATSAPP_ENABLED, PHONE_ENABLED } from "../../config";
import { track } from "../../lib/tracking";

const links = [
  { label: "Servizi", href: "#servizi" },
  { label: "Perché noi", href: "#perche" },
  { label: "Galleria", href: "#galleria" },
  { label: "Recensioni", href: "#recensioni" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-gray-100 shadow-sm" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 h-16 sm:h-[72px] flex items-center justify-between gap-4">
        <a href="#top" data-testid="nav-logo" aria-label="Muratore Imbianchino Roma">
          <Logo />
        </a>

        <ul className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="text-sm font-semibold text-ink/80 hover:text-navy transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-brand-orange hover:after:w-full after:transition-[width] after:duration-300"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 sm:gap-3">
          {PHONE_ENABLED && (
            <a
              href={`tel:${CONTACT.phoneNumber}`}
              data-testid="nav-phone"
              aria-label="Chiama"
              onClick={() => track.phoneClick("navbar")}
              className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-navy/70 hover:text-navy hover:border-navy/30 transition-colors"
            >
              <Phone size={17} />
            </a>
          )}
          {WHATSAPP_ENABLED ? (
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp"
              onClick={() => track.whatsappClick("navbar")}
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 sm:px-5 py-2.5 text-sm font-semibold text-white shadow-[0_6px_18px_-6px_rgba(37,211,102,0.8)] hover:bg-[#1ebe5b] hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
            >
              <WhatsAppIcon size={18} />
              <span className="hidden xs:inline sm:inline">WhatsApp</span>
            </a>
          ) : (
            <a
              href="#preventivo"
              data-testid="nav-preventivo"
              className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-4 sm:px-5 py-2.5 text-sm font-semibold text-white shadow-[0_6px_18px_-6px_rgba(242,140,40,0.8)] hover:bg-brand-orange-dark hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
            >
              Preventivo
            </a>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-menu-toggle"
            aria-label="Menu"
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-gray-200 text-navy"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white border-b border-gray-100"
          >
            <ul className="px-4 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                    className="block px-3 py-3 rounded-lg text-base font-semibold text-ink hover:bg-surface"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li className="mt-2">
                <a
                  href="#preventivo"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-preventivo"
                  className="block text-center px-3 py-3 rounded-full bg-brand-orange text-white font-semibold"
                >
                  Richiedi Preventivo
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
